import { AttachFile } from "@mui/icons-material";
import { Chip, CircularProgress, Stack, Tooltip } from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";
import { getApiError } from "utils/apiError";
import { attachmentDisplayName } from "utils/emailAttachments";
import { fetchEmailAttachmentUrl } from "utils/emailLogService";

/**
 * Attachment chips for a logged email. Clicking opens the file in a new tab
 * via a short-lived presigned URL. Without a log id (e.g. an unsent draft)
 * the chips render as plain labels.
 */
const EmailAttachmentChips = ({ logId, attachmentKeys }) => {
  const [openingIndex, setOpeningIndex] = useState(null);

  if (!attachmentKeys?.length) return null;

  const openAttachment = async (key, index) => {
    if (!logId || openingIndex !== null) return;
    setOpeningIndex(index);
    try {
      const url = await fetchEmailAttachmentUrl(logId, index);
      if (!url) throw new Error("empty link");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(getApiError(err, "Could not open this attachment. Please try again."));
    } finally {
      setOpeningIndex(null);
    }
  };

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      {attachmentKeys.map((key, index) => {
        const label = attachmentDisplayName(key);
        const opening = openingIndex === index;
        const chip = (
          <Chip
            key={`${key}-${index}`}
            size="small"
            icon={opening ? <CircularProgress size={12} /> : <AttachFile />}
            label={opening ? `Opening ${label}…` : label}
            variant="outlined"
            onClick={logId ? () => openAttachment(key, index) : undefined}
            sx={logId ? { cursor: "pointer" } : undefined}
          />
        );
        return logId ? (
          <Tooltip key={`${key}-${index}-tip`} title={`Open ${label}`}>
            <span>{chip}</span>
          </Tooltip>
        ) : (
          chip
        );
      })}
    </Stack>
  );
};

export default EmailAttachmentChips;
